import type { Slot } from 'vue'
import TableOverflowText from '@/components/TableOverflowText/index.vue'
import { ElTableColumn } from 'element-plus'
import { defineComponent, h, useAttrs } from 'vue'

type TextLines = 1 | 2

// 数值、日期、金额和状态列不应用长文本截断规则。
const EXCLUDED_TOKENS = new Set([
  'amount',
  'balance',
  'cpi',
  'count',
  'currency',
  'date',
  'fee',
  'ir',
  'loi',
  'money',
  'price',
  'rate',
  'ratio',
  'status',
  'tax',
  'time',
  'total',
  'wallet',
])

// 标识类字段使用单行省略，例如 ID、编号、URL、邮箱和账号。
const SINGLE_LINE_TOKENS = new Set([
  'account',
  'code',
  'email',
  'id',
  'ip',
  'mail',
  'number',
  'phone',
  'telephone',
  'uid',
  'url',
  'username',
  'uuid',
])

// 语义文本类字段最多显示两行，例如名称、标题、备注和描述。
const TWO_LINE_TOKENS = new Set([
  'address',
  'content',
  'desc',
  'description',
  'detail',
  'message',
  'name',
  'note',
  'reason',
  'remark',
  'title',
])

// 将 projectName、project_name 等不同命名方式拆分为统一的小写词元。
function getFieldTokens(field: string) {
  const leaf = field.split('.').at(-1) || ''
  return leaf
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[^a-z0-9]+/i)
    .map(token => token.toLowerCase())
    .filter(Boolean)
}

// 根据字段名决定公共文本组件使用单行还是双行模式。
function getTextLines(field: unknown): TextLines | undefined {
  if (typeof field !== 'string' || !field) {
    return undefined
  }

  const tokens = getFieldTokens(field)
  if (tokens.some(token => EXCLUDED_TOKENS.has(token))) {
    return undefined
  }
  if (tokens.some(token => TWO_LINE_TOKENS.has(token))) {
    return 2
  }
  if (tokens.some(token => SINGLE_LINE_TOKENS.has(token))) {
    return 1
  }

  return undefined
}

// 支持 project.client.name 这类嵌套字段路径。
function getValueByPath(row: unknown, path: string) {
  if (!row || typeof row !== 'object') {
    return ''
  }

  return path.split('.').reduce<unknown>((value, key) => {
    if (!value || typeof value !== 'object') {
      return undefined
    }
    return (value as Record<string, unknown>)[key]
  }, row)
}

export default defineComponent({
  name: 'UnifiedTableColumn',
  inheritAttrs: false,
  setup(_, { slots }) {
    const attrs = useAttrs()

    return () => {
      const field = typeof attrs.prop === 'string' ? attrs.prop : ''
      const lines = getTextLines(field)
      const columnSlots = { ...slots } as Record<string, Slot | undefined>

      // 只接管没有自定义单元格模板的列，避免改变原页面的复合内容布局。
      if (lines && !slots.default) {
        columnSlots.default = (({ row }: { row: unknown }) => [
          h(TableOverflowText, {
            lines,
            text: getValueByPath(row, field) as string | number | null | undefined,
          }),
        ]) as Slot
      }

      return h(ElTableColumn, attrs, columnSlots)
    }
  },
})
